import { useEffect, useRef, useState, type RefObject } from "react";
import type { Editor } from "@tiptap/react";

/**
 * O cursor de texto fluido do caderno — seção 8 da identidade visual.
 *
 * O cursor nativo pisca em degrau; o do caderno **desliza**. O nativo fica
 * escondido com `caret-color: transparent` e um elemento próprio é desenhado
 * sobre o texto, animado por `requestAnimationFrame` até a posição do cursor
 * real.
 *
 * **Sem biblioteca**, e por CSSOM. A posição sai por
 * `setProperty("--cursor-x", …)`, que a CSP permite: o que `style-src-attr`
 * bloqueia é o **atributo** `style` vindo de marcação, não a escrita
 * programática de uma custom property. A string do atributo inteiro nunca é
 * montada aqui — é o mesmo caminho que a RN-40 proíbe no conteúdo, e ele também
 * não passa neste arquivo.
 *
 * **`prefers-reduced-motion` desde a primeira linha.** Sob ele o elemento nem é
 * renderizado e o cursor nativo volta inteiro — a variante estática funcionando
 * de verdade, e não uma animação de duração zero. A folha diz o mesmo por conta
 * própria (`base.css`), de modo que a preferência vale mesmo que este estado
 * fique para trás.
 *
 * **O laço só roda enquanto o cursor desliza.** Ao assentar ele para e entrega a
 * piscada a uma animação CSS — 60fps parado na thread principal seria o caso que
 * a diretriz de movimento existe para evitar, e a tela de escrita é onde se
 * digita centenas de vezes.
 */

const MOVIMENTO_REDUZIDO = "(prefers-reduced-motion: reduce)";

/** Quanto da distância que falta o cursor vence por quadro. */
const APROXIMACAO = 0.32;

/** Abaixo de meio pixel o deslize acabou: o olho não vê, e o laço para. */
const PARADO_EM = 0.5;

type Ponto = { x: number; y: number; altura: number };

export function useCursorFluido(editor: Editor | null): {
  /** O cursor próprio está no ar; sob movimento reduzido isto é `false`. */
  fluido: boolean;
  /** A caixa de referência da posição — precisa envolver a área de escrita. */
  superficie: RefObject<HTMLDivElement | null>;
  /** O cursor desenhado. */
  cursor: RefObject<HTMLSpanElement | null>;
} {
  const superficie = useRef<HTMLDivElement>(null);
  const cursor = useRef<HTMLSpanElement>(null);
  const [fluido, setFluido] = useState(() => !window.matchMedia(MOVIMENTO_REDUZIDO).matches);

  useEffect(() => {
    const mq = window.matchMedia(MOVIMENTO_REDUZIDO);
    const aoMudar = () => setFluido(!mq.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  useEffect(() => {
    const caixa = superficie.current;
    const marca = cursor.current;
    if (!editor || !fluido || !caixa || !marca) return;

    let alvo: Ponto | null = null;
    let atual: Ponto | null = null;
    let quadro = 0;

    /*
     * `coordsAtPos` é medida do navegador e pode recusar uma posição que a vista
     * ainda não desenhou — entre a transação e a pintura, por exemplo. Recusa
     * não é erro: é só um quadro sem medida nova.
     */
    const medir = (): Ponto | null => {
      if (editor.isDestroyed) return null;
      try {
        const c = editor.view.coordsAtPos(editor.state.selection.head);
        const r = caixa.getBoundingClientRect();
        return { x: c.left - r.left, y: c.top - r.top, altura: Math.max(c.bottom - c.top, 1) };
      } catch {
        return null;
      }
    };

    const pintar = (p: Ponto) => {
      marca.style.setProperty("--cursor-x", `${p.x.toFixed(1)}px`);
      marca.style.setProperty("--cursor-y", `${p.y.toFixed(1)}px`);
      marca.style.setProperty("--cursor-altura", `${p.altura.toFixed(1)}px`);
    };

    const deslizar = () => {
      quadro = 0;
      if (!alvo || !atual) return;

      const dx = alvo.x - atual.x;
      const dy = alvo.y - atual.y;
      if (Math.abs(dx) < PARADO_EM && Math.abs(dy) < PARADO_EM) {
        atual = alvo;
        pintar(atual);
        // Tirar a classe devolve a animação da folha, e animação que volta a
        // valer recomeça do zero: é assim que a piscada nasce inteira a cada
        // parada, sem ninguém remontar nada.
        marca.classList.remove("editor-rico__cursor--movendo");
        return;
      }

      atual = { x: atual.x + dx * APROXIMACAO, y: atual.y + dy * APROXIMACAO, altura: alvo.altura };
      pintar(atual);
      quadro = requestAnimationFrame(deslizar);
    };

    const seguir = (saltar = false) => {
      const medida = medir();
      if (!medida) return;
      alvo = medida;

      // A primeira posição não desliza de lugar nenhum: ela aparece onde está.
      if (saltar || !atual) {
        atual = medida;
        pintar(atual);
        return;
      }

      marca.classList.add("editor-rico__cursor--movendo");
      if (!quadro) quadro = requestAnimationFrame(deslizar);
    };

    const aoFocar = () => {
      marca.classList.add("editor-rico__cursor--visivel");
      seguir(true);
    };
    const aoDesfocar = () => {
      marca.classList.remove("editor-rico__cursor--visivel");
      marca.classList.remove("editor-rico__cursor--movendo");
    };
    const aoTransacionar = () => seguir();
    // Mudou a caixa, mudou a quebra de linha: a medida antiga aponta para o
    // lugar errado, e saltar é mais honesto que deslizar para lá.
    const aoRedimensionar = () => seguir(true);

    editor.on("transaction", aoTransacionar);
    editor.on("focus", aoFocar);
    editor.on("blur", aoDesfocar);
    window.addEventListener("resize", aoRedimensionar);

    if (editor.isFocused) aoFocar();

    return () => {
      editor.off("transaction", aoTransacionar);
      editor.off("focus", aoFocar);
      editor.off("blur", aoDesfocar);
      window.removeEventListener("resize", aoRedimensionar);
      if (quadro) cancelAnimationFrame(quadro);
    };
  }, [editor, fluido]);

  return { fluido, superficie, cursor };
}
